import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, X } from 'lucide-react';
import { logger } from '../../utils/logger';

export function VersionChecker() {
  const { t } = useTranslation('common');
  const [hasUpdate, setHasUpdate] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);

  useEffect(() => {
    // 1. Fetch initial version on load
    const checkVersion = async (isInitial = false) => {
      try {
        // Add timestamp to prevent caching of version.json itself
        const res = await fetch(`/version.json?t=${Date.now()}`);
        if (!res.ok) return;
        
        const data = await res.json();
        const latestVersion = data.version;

        if (isInitial) {
          setCurrentVersion(latestVersion);
        } else if (currentVersion && latestVersion !== currentVersion) {
          setHasUpdate(true);
        }
      } catch (error) {
        logger.error('Failed to check version', error);
      }
    };

    // Initial check
    checkVersion(true);

    // 2. Poll every 5 minutes
    const interval = setInterval(() => checkVersion(), 5 * 60 * 1000);

    // 3. Check when window regains focus
    const onFocus = () => checkVersion();
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [currentVersion]);

  const handleRefresh = () => {
    window.location.reload();
  };

  if (!hasUpdate) return null;

  return (
    <div 
      className="fixed top-0 left-0 right-0 z-2000 bg-blue-600 text-white shadow-lg transition-transform transform translate-y-0 cursor-pointer"
      onClick={handleRefresh}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleRefresh();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={t('version.newVersion')}
    >
      <div className="w-full px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-start">
        <div className="flex items-center justify-center sm:justify-start space-x-3 flex-1">
          <RefreshCw className="w-5 h-5 animate-spin-slow shrink-0" />
          <span className="font-medium text-sm sm:text-base">
            {t('version.newVersion')}
          </span>
        </div>
        <div className="flex items-center space-x-4 shrink-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleRefresh();
            }}
            className="bg-white text-blue-600 px-4 py-1.5 rounded-full text-sm font-bold hover:bg-blue-50 transition-colors whitespace-nowrap"
          >
            {t('version.refresh')}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setHasUpdate(false);
            }}
            className="p-1 hover:bg-blue-700 rounded-full transition-colors"
            aria-label={t('actions.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
